/**
 * VendorLogin — public vendor re-login screen.
 * Route: /vendor/login
 */
import { useState, FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { vendorLogin, storeVendorSession } from '../../api/vendor'
import { useAuth } from '../../context/AuthContext'
import { AuthBrandHeader, AuthFooter } from '../../components/auth/AuthBrandHeader'

export default function VendorLogin() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { refetch } = useAuth()
  const loggedOutNote = searchParams.get('loggedOut') === '1'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const { token } = await vendorLogin(email.trim(), password)
      storeVendorSession(token)
      await refetch()
      navigate('/vendor/dashboard', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid email or password')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="vendor-portal"
      data-testid="vendor-login"
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 380 }}>
        <AuthBrandHeader />
        <div
          style={{
            width: '100%',
            background: '#fff',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r)',
            padding: '28px 24px',
          }}
        >
        <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', margin: '0 0 6px' }}>
          Vendor sign in
        </h1>
        <p style={{ fontSize: 12, color: 'var(--ink4)', margin: '0 0 16px', lineHeight: 1.5 }}>
          Sign in to upload documents and view your projects.
        </p>

        {loggedOutNote && (
          <p
            data-testid="vendor-logged-out-note"
            style={{
              fontSize: 13,
              color: 'var(--ink2)',
              background: 'var(--vp-accent-soft, #f5f0e8)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--rs)',
              padding: '10px 12px',
              margin: '0 0 16px',
              lineHeight: 1.45,
            }}
          >
            You&apos;ve been logged out.
          </p>
        )}

        <form onSubmit={handleSubmit}>
          <label style={{ display: 'block', marginBottom: 14 }}>
            <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 6 }}>
              Email
            </span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="fld-i"
              style={{ width: '100%', padding: '10px 12px', fontSize: 14 }}
            />
          </label>

          <label style={{ display: 'block', marginBottom: 10 }}>
            <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 6 }}>
              Password
            </span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="fld-i"
              style={{ width: '100%', padding: '10px 12px', fontSize: 14 }}
            />
          </label>

          <p style={{ margin: '0 0 18px', textAlign: 'right' }}>
            <Link
              to="/vendor/forgot-password"
              data-testid="vendor-forgot-password-link"
              style={{ fontSize: 12, color: 'var(--ink3)', textDecoration: 'none', fontWeight: 600 }}
            >
              Forgot password?
            </Link>
          </p>

          {error && (
            <p style={{ fontSize: 12, color: 'var(--red)', margin: '0 0 14px' }} role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="btn b-dk"
            disabled={submitting}
            style={{ width: '100%', justifyContent: 'center', padding: '10px 16px', fontWeight: 600 }}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        </div>
        <AuthFooter />
      </div>
    </div>
  )
}
